import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useGameData } from "./GameContext";
import { gameDetails } from "../App";
import { useLanguage } from "../context/LanguageContext";
import Navbar from "../components/Navbar";

const GamesPage: React.FC = () => {
    const navigate = useNavigate();
    const { gameData, refreshGameData, lastRefreshed, isLoading } = useGameData();
    const { t } = useLanguage();

    const handleGameClick = (gameKey: string) => {
        navigate(`/users/${gameKey}`);
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />
            <main className="pt-16 pb-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center mb-6">
                        <h1 className="text-2xl font-bold text-gray-800">{t("games")}</h1>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {Object.entries(gameData).map(([gameKey, sessions], index) => {
                            const { name: gameName, logo } = gameDetails[gameKey];
                            const totalUsers = sessions.length;
                            const totalSessionTime = sessions.reduce((sum, session) => sum + (session.sessionTime || 0), 0);
                            const avgSessionTime = totalUsers > 0 ? totalSessionTime / totalUsers : 0;
                            const purchases = sessions.filter(session => session.purchaseClicked).length;
                            const confirmations = sessions.filter(session => session.confirmClicked).length;

                            return (
                                <motion.div
                                    key={gameKey}
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.1, ease: "easeOut" }}
                                    className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
                                >
                                    <div className="p-6">
                                        <div className="flex justify-between items-center mb-4">
                                            <div className="flex items-center space-x-3">
                                                <img src={logo} alt={gameName} className="w-12 h-12 rounded-full" />
                                                <div>
                                                    <h2 className="text-lg font-semibold text-gray-800">{gameName}</h2>
                                                    <p className="text-sm text-gray-500">
                                                        {t("total_users")}: {totalUsers}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                {lastRefreshed[gameKey] && (
                                                    <span className="text-sm text-gray-500">
                                                        {t("last_updated")}: {lastRefreshed[gameKey]?.toLocaleTimeString()}
                                                    </span>
                                                )}
                                                <button
                                                    onClick={() => refreshGameData(gameKey)}
                                                    disabled={isLoading[gameKey]}
                                                    className={`p-2 rounded-lg ${
                                                        isLoading[gameKey]
                                                        ? "bg-gray-400 cursor-not-allowed" 
                                                        : "bg-blue-500 hover:bg-blue-600"
                                                    }`}
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4 mb-4">
                                            <div className="bg-gray-50 p-3 rounded-lg">
                                                <p className="text-sm text-gray-500">{t("avg_session_time")}</p>
                                                <p className="text-lg font-semibold">
                                                    {Math.floor(avgSessionTime / 60)} {t("minutes")}
                                                </p>
                                            </div>
                                            <div className="bg-gray-50 p-3 rounded-lg">
                                                <p className="text-sm text-gray-500">{t("purchases")}</p>
                                                <p className="text-lg font-semibold">{purchases}</p>
                                            </div>
                                            <div className="bg-gray-50 p-3 rounded-lg">
                                                <p className="text-sm text-gray-500">{t("confirmations")}</p>
                                                <p className="text-lg font-semibold">{confirmations}</p>
                                            </div>
                                            <div className="bg-gray-50 p-3 rounded-lg">
                                                <p className="text-sm text-gray-500">{t("conversion_rate")}</p>
                                                <p className="text-lg font-semibold">
                                                    {totalUsers > 0 ? ((purchases / totalUsers) * 100).toFixed(1) : 0}%
                                                </p>
                                            </div>
                                        </div>
                                        <motion.button
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 text-white py-2 rounded-lg font-medium hover:from-blue-600 hover:to-indigo-600 transition-all duration-200"
                                            onClick={() => handleGameClick(gameKey)}
                                        >
                                            {t("view_details")}
                                        </motion.button>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default GamesPage; 